/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useMemo, useCallback, memo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useCompany } from "@/hooks/useCompany";
import { useCompanyPackageAccess } from "@/hooks/useCompanyPackageAccess";
import { useCompanyUsage } from "@/hooks/useCompanyUsage";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Plus, Factory, Edit2, Trash2, Eye, ChevronLeft, ChevronRight,
  Newspaper, Clock, AlertCircle, CheckCircle2, FileText, ExternalLink, Rocket,
  ImagePlus, Loader2,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { enUS } from "date-fns/locale";
import ArticleFilters from "@/components/articles/ArticleFilters";
import ArticleBulkBar from "@/components/articles/ArticleBulkBar";
import ArticleQuickPreview from "@/components/articles/ArticleQuickPreview";
import StatusPicker, { type ArticleStatus } from "@/components/articles/StatusPicker";
import MultiSitePublishDialog, { type PublishWebsite } from "@/components/publishing/MultiSitePublishDialog";

const tenantDb = supabase as any;

const trialDemoArticles = [
  {
    id: "trial-demo-brief",
    title: "Trial sample - Daily editorial brief",
    subtitle: "A read-only example of how incoming topics are prepared for review.",
    excerpt: "This temporary record shows the article library layout without creating production content.",
    content: "This temporary record shows the article library layout without creating production content.",
    status: "review",
    language: "en",
    updated_at: new Date().toISOString(),
    categories: { name: "Demo" },
    authors: { name: "Delapresse" },
    profiles: { full_name: "Delapresse" },
    article_publications: [],
    is_trial_demo: true,
  },
  {
    id: "trial-demo-approved",
    title: "Trial sample - Approved article",
    subtitle: "A read-only approved article example for trial workspaces.",
    excerpt: "Trial accounts can inspect the editorial workflow before choosing a paid plan.",
    content: "Trial accounts can inspect the editorial workflow before choosing a paid plan.",
    status: "approved",
    language: "en",
    updated_at: new Date(Date.now() - 36 * 60 * 60 * 1000).toISOString(),
    categories: { name: "Demo" },
    authors: { name: "Delapresse" },
    profiles: { full_name: "Delapresse" },
    article_publications: [],
    is_trial_demo: true,
  },
  {
    id: "trial-demo-published",
    title: "Trial sample - Published preview",
    subtitle: "A read-only published-state example.",
    excerpt: "Use these temporary rows to understand article status, preview, and review states.",
    content: "Use these temporary rows to understand article status, preview, and review states.",
    status: "published",
    language: "en",
    updated_at: new Date(Date.now() - 72 * 60 * 60 * 1000).toISOString(),
    published_at: new Date(Date.now() - 72 * 60 * 60 * 1000).toISOString(),
    categories: { name: "Demo" },
    authors: { name: "Delapresse" },
    profiles: { full_name: "Delapresse" },
    article_publications: [],
    is_trial_demo: true,
  },
];

const STATUS_CONFIG: Record<string, { label: string; icon: React.ReactNode; classes: string }> = {
  draft: {
    label: "Draft",
    icon: <FileText className="h-3 w-3" />,
    classes: "bg-muted text-muted-foreground border-transparent",
  },
  review: {
    label: "In Review",
    icon: <Eye className="h-3 w-3" />,
    classes: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 border-transparent",
  },
  revision: {
    label: "Revision",
    icon: <Edit2 className="h-3 w-3" />,
    classes: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400 border-transparent",
  },
  approved: {
    label: "Approved",
    icon: <CheckCircle2 className="h-3 w-3" />,
    classes: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400 border-transparent",
  },
  scheduled: {
    label: "Scheduled",
    icon: <Clock className="h-3 w-3" />,
    classes: "bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-400 border-transparent",
  },
  published: {
    label: "Published",
    icon: <CheckCircle2 className="h-3 w-3" />,
    classes: "bg-emerald-600 text-white dark:bg-emerald-600 dark:text-white border-transparent",
  },
  archived: {
    label: "Archived",
    icon: <AlertCircle className="h-3 w-3" />,
    classes: "bg-destructive/15 text-destructive border-transparent",
  },
};

function timeAgo(date: string) {
  try {
    return formatDistanceToNow(new Date(date), { addSuffix: true, locale: enUS });
  } catch {
    return "—";
  }
}

function articleDescription(article: any) {
  const text = article.excerpt || article.subtitle || article.content || "";
  return String(text).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

// Row component extracted + memoised. Without this, every state change in
// ArticlesPage (filter input, mutation pending state, preview dialog open)
// would re-render every row in the list. The memo gates re-renders to
// real prop changes (article object / isSelected / updateStatusLoading).
//
// All event handlers are received as stable useCallback references from
// the parent so React.memo's shallow comparison actually short-circuits.
interface ArticleRowProps {
  article: any;
  isSelected: boolean;
  updateStatusLoading: boolean;
  onToggleSelect: (id: string) => void;
  onChangeStatus: (id: string, status: ArticleStatus) => void;
  onPreview: (article: any) => void;
  onSendToFactory: (id: string) => void;
  onPublishSingle: (id: string) => void;
  onDelete: (id: string) => void;
}

const ArticleRow = memo(function ArticleRow({
  article,
  isSelected,
  updateStatusLoading,
  onToggleSelect,
  onChangeStatus,
  onPreview,
  onSendToFactory,
  onPublishSingle,
  onDelete,
}: ArticleRowProps) {
  const { t } = useTranslation();
  const pubs = article.article_publications || [];
  const isPublished = article.status === "published";
  const isTrialDemo = Boolean(article.is_trial_demo);

  return (
    <TableRow
      data-state={isSelected ? "selected" : undefined}
      className="group transition-colors hover:bg-muted/60"
    >
      <TableCell className="py-2">
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => onToggleSelect(article.id)}
          aria-label={`Select ${article.title}`}
          disabled={isTrialDemo}
        />
      </TableCell>

      <TableCell className="py-2">
        <div className="h-10 w-10 rounded-md overflow-hidden bg-muted flex items-center justify-center flex-shrink-0">
          {article.featured_image_url ? (
            <img
              src={article.featured_image_url}
              alt=""
              className="h-full w-full object-cover"
              loading="lazy"
            />
          ) : (
            <Newspaper className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      </TableCell>

      <TableCell className="py-2 align-top">
        {isTrialDemo ? (
          <span className="block font-medium leading-snug text-foreground whitespace-normal break-words">{article.title}</span>
        ) : (
          <Link to={`/articles/${article.id}`} className="block font-medium leading-snug text-foreground hover:text-primary transition-colors whitespace-normal break-words">
            {article.title}
          </Link>
        )}
        {articleDescription(article) && (
          <p className="mt-0.5 text-xs leading-snug text-muted-foreground line-clamp-1">
            {articleDescription(article)}
          </p>
        )}
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
          <span className="font-medium text-foreground/80">{article.categories?.name || "Uncategorized"}</span>
          {article.authors?.name && (
            <Avatar className="h-4 w-4">
              <AvatarFallback className="text-[7px] font-medium">{(article.authors.name || "?").split(" ").map((w: string) => w[0]).join("").slice(0,2)}</AvatarFallback>
            </Avatar>
          )}
          <span>{article.authors?.name || article.profiles?.full_name || "No author"}</span>
          {article.language && (
            <Badge variant="outline" className="text-[9px] px-1 py-0 h-4 uppercase shrink-0">
              {article.language}
            </Badge>
          )}
          {isTrialDemo && <Badge variant="secondary" className="text-[9px] px-1 py-0 h-4">Temporary</Badge>}
          <span title={article.updated_at}>{timeAgo(article.updated_at)}</span>
          {pubs.length > 0 ? (
            <span className="flex min-w-0 flex-wrap items-center gap-1">
              <span>Published:</span>
              {pubs.slice(0, 4).map((p: any) => {
                const siteName = p.websites?.name;
                const siteDomain = p.websites?.domain;
                if (!siteName) return null;
                return (
                  <Link
                    key={p.website_id}
                    to={`/websites/${p.website_id}`}
                    className="max-w-[9rem] truncate text-primary hover:underline"
                    title={siteDomain || siteName}
                  >
                    {siteName}
                  </Link>
                );
              })}
              {pubs.length > 4 && <span>+{pubs.length - 4}</span>}
            </span>
          ) : (
            <span>Not published</span>
          )}
        </div>
      </TableCell>

      <TableCell className="py-2 align-top">
        {isTrialDemo ? (
          <Badge variant="outline" className="capitalize">{String(article.status).replaceAll("_", " ")}</Badge>
        ) : (
          <StatusPicker
            value={article.status}
            onChange={(next) => onChangeStatus(article.id, next)}
            loading={updateStatusLoading}
          />
        )}
      </TableCell>

      <TableCell className="py-2 align-top">
        <div className="flex items-center justify-end gap-1">
          <IconButton variant="ghost" className="h-7 w-7" onClick={() => onPreview(article)} label={t("actions.preview")}>
            <Eye className="h-3.5 w-3.5" />
          </IconButton>
          {isPublished && pubs.length > 0 && pubs[0]?.websites?.domain && (
            <a href={`https://${pubs[0].websites.domain}`} target="_blank" rel="noopener noreferrer">
              <IconButton variant="ghost" className="h-7 w-7" label={t("actions.viewLive")}>
                <ExternalLink className="h-3.5 w-3.5" />
              </IconButton>
            </a>
          )}
          {!isTrialDemo && article.status === "draft" && (
            <IconButton
              variant="ghost"
              className="h-7 w-7"
              onClick={() => onSendToFactory(article.id)}
              label={t("articles.sendToPipeline", { defaultValue: "Send to pipeline" })}
            >
              <Factory className="h-3.5 w-3.5" />
            </IconButton>
          )}
          {!isTrialDemo && (
            <>
              <IconButton
                variant="ghost"
                className="h-7 w-7 text-success hover:text-success"
                label={t("articles.publishToWebsites", { defaultValue: "Publish to websites" })}
                onClick={() => onPublishSingle(article.id)}
              >
                <Rocket className="h-3.5 w-3.5" />
              </IconButton>
              <Link to={`/articles/${article.id}`}>
                <IconButton variant="ghost" className="h-7 w-7" label={t("actions.edit")}>
                  <Edit2 className="h-3.5 w-3.5" />
                </IconButton>
              </Link>
            </>
          )}
          {!isTrialDemo && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <IconButton variant="ghost" className="h-7 w-7 text-destructive hover:text-destructive" label={t("actions.delete")}>
                  <Trash2 className="h-3.5 w-3.5" />
                </IconButton>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this article?</AlertDialogTitle>
                  <AlertDialogDescription>
                    "{article.title}" will be permanently deleted.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => onDelete(article.id)}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
});

export default function ArticlesPage() {
  const { user } = useAuth();
  const { currentCompanyId, isLoading: companyLoading } = useCompany();
  const { package: packageInfo, canCreateArticles, articleLimit } = useCompanyPackageAccess();
  const { usage } = useCompanyUsage();
  const { toast } = useToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get("page") || "1");
  const perPage = Number(searchParams.get("perPage") || "20");
  const search = searchParams.get("q") || "";
  const filterStatus = searchParams.get("status") || "all";
  const filterCategory = searchParams.get("category") || "all";
  const filterWebsite = searchParams.get("website") || "all";
  const filterAuthor = searchParams.get("author") || "all";
  const filterLanguage = searchParams.get("lang") || "all";

  const setParam = useCallback((key: string, value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === "" || value === "all" || (key === "page" && value === "1") || (key === "perPage" && value === "20")) {
        next.delete(key);
      } else {
        next.set(key, value);
      }
      if (key !== "page") next.delete("page");
      return next;
    });
  }, [setSearchParams]);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [previewArticle, setPreviewArticle] = useState<any>(null);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);

  const backfillImages = useMutation({
    mutationFn: async (ids: string[] | null) => {
      const { data, error } = await supabase.functions.invoke("backfill-article-images", {
        body: ids ? { article_ids: ids } : { limit: 50 },
      });
      if (error) throw error;
      return data as { processed: number; assigned: number; skipped: number; failed: number };
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["articles-library"] });
      toast({
        title: `${res.assigned} image(s) assigned`,
        description: `${res.processed} processed · ${res.skipped} skipped · ${res.failed} failed`,
      });
    },
    onError: (err: any) => {
      toast({ title: "Auto-assign failed", description: err.message, variant: "destructive" });
    },
  });

  const { data: categories = [] } = useQuery({
    queryKey: ["categories-list"],
    queryFn: async () => {
      const { data } = await supabase.from("categories").select("id, name").order("name");
      return data || [];
    },
    enabled: !!user,
  });

  const { data: websites = [] } = useQuery({
    queryKey: ["websites-list", currentCompanyId],
    queryFn: async () => {
      const { data } = await tenantDb.from("websites").select("id, name, domain, primary_language").eq("company_id", currentCompanyId).order("name");
      return (data || []) as PublishWebsite[];
    },
    enabled: !!user && !!currentCompanyId,
  });

  const { data: authors = [] } = useQuery({
    queryKey: ["authors-list"],
    queryFn: async () => {
      const { data } = await supabase.from("profiles").select("id, full_name");
      return data || [];
    },
    enabled: !!user,
  });

  const { data: queryResult, isLoading } = useQuery({
    queryKey: ["articles-library", currentCompanyId, page, perPage, search, filterStatus, filterCategory, filterWebsite, filterAuthor, filterLanguage],
    queryFn: async () => {
      let query = tenantDb
        .from("articles")
        .select("*, profiles(full_name), categories(name), authors(name, avatar_url), article_publications(website_id, status, websites(name, domain))", { count: "exact" })
        .eq("company_id", currentCompanyId);

      if (search) {
        query = query.or(`title.ilike.%${search}%,content.ilike.%${search}%`);
      }
      if (filterStatus !== "all") query = query.eq("status", filterStatus as any);
      if (filterCategory !== "all") query = query.eq("category_id", filterCategory);
      if (filterWebsite !== "all") query = query.eq("website_id", filterWebsite);
      if (filterAuthor !== "all") query = query.eq("author_id", filterAuthor);
      if (filterLanguage !== "all") query = query.eq("language", filterLanguage);

      const from = (page - 1) * perPage;
      const to = from + perPage - 1;

      const { data, error, count } = await query
        .order("updated_at", { ascending: false })
        .range(from, to);

      if (error) throw error;
      return { articles: data || [], total: count || 0 };
    },
    enabled: !!user && !!currentCompanyId && !companyLoading,
  });

  // Stable reference for the empty-array fallback so downstream useMemo
  // hooks (selectedArticles, selectedLanguages) don't invalidate every render.
  const realArticles = useMemo(() => queryResult?.articles ?? [], [queryResult?.articles]);
  const isTrialPackage = Boolean(packageInfo?.is_trial);
  const articles = useMemo(
    () => (isTrialPackage && realArticles.length === 0 ? trialDemoArticles : realArticles),
    [isTrialPackage, realArticles],
  );
  const totalCount = queryResult?.total || (isTrialPackage && realArticles.length === 0 ? trialDemoArticles.length : 0);
  const totalPages = Math.max(1, Math.ceil(totalCount / perPage));

  const bulkDelete = useMutation({
    mutationFn: async (ids: string[]) => {
      const { error } = await tenantDb.from("articles").delete().in("id", ids).eq("company_id", currentCompanyId);
      if (error) throw error;
    },
    onSuccess: () => {
      setSelected(new Set());
      queryClient.invalidateQueries({ queryKey: ["articles-library"] });
      toast({ title: "Articles deleted" });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const singleDelete = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await tenantDb.from("articles").delete().eq("id", id).eq("company_id", currentCompanyId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["articles-library"] });
      toast({ title: "Article deleted" });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const sendToFactory = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await tenantDb.from("articles").update({ status: "review" as any }).eq("id", id).eq("company_id", currentCompanyId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["articles-library"] });
      toast({ title: "Sent to pipeline" });
    },
  });

  const updateStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: ArticleStatus }) => {
      const patch: any = { status };
      if (status === "published") patch.published_at = new Date().toISOString();
      const { data, error } = await tenantDb
        .from("articles")
        .update(patch)
        .eq("id", id)
        .eq("company_id", currentCompanyId)
        .select("id");
      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error("You don't have permission to update this article (only the author can change its status).");
      }
    },
    onMutate: async ({ id, status }) => {
      await queryClient.cancelQueries({ queryKey: ["articles-library"] });
      const previous = queryClient.getQueriesData({ queryKey: ["articles-library"] });
      queryClient.setQueriesData({ queryKey: ["articles-library"] }, (old: any) => {
        if (!old) return old;
        if (Array.isArray(old)) {
          return old.map((a: any) => (a.id === id ? { ...a, status } : a));
        }
        if (old.items && Array.isArray(old.items)) {
          return { ...old, items: old.items.map((a: any) => (a.id === id ? { ...a, status } : a)) };
        }
        if (old.data && Array.isArray(old.data)) {
          return { ...old, data: old.data.map((a: any) => (a.id === id ? { ...a, status } : a)) };
        }
        return old;
      });
      return { previous };
    },
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: ["articles-library"] });
      toast({ title: `Status updated to ${vars.status}` });
    },
    onError: (err: any, _vars, ctx: any) => {
      // rollback optimistic update
      if (ctx?.previous) {
        ctx.previous.forEach(([key, data]: any) => queryClient.setQueryData(key, data));
      }
      toast({ title: "Couldn't update status", description: err.message, variant: "destructive" });
    },
  });

  const bulkUpdateStatus = useMutation({
    mutationFn: async ({ ids, status }: { ids: string[]; status: ArticleStatus }) => {
      const patch: any = { status };
      if (status === "published") patch.published_at = new Date().toISOString();
      const { data, error } = await tenantDb
        .from("articles")
        .update(patch)
        .in("id", ids)
        .eq("company_id", currentCompanyId)
        .select("id");
      if (error) throw error;
      const updatedCount = data?.length ?? 0;
      if (updatedCount === 0) {
        throw new Error("None of the selected articles could be updated (you can only change articles you authored).");
      }
      return { updatedCount, requested: ids.length };
    },
    onSuccess: (res, vars) => {
      setSelected(new Set());
      queryClient.invalidateQueries({ queryKey: ["articles-library"] });
      const skipped = res.requested - res.updatedCount;
      toast({
        title: `${res.updatedCount} article(s) moved to ${vars.status}`,
        description: skipped > 0 ? `${skipped} skipped (not your articles).` : undefined,
      });
    },
    onError: (err: any) => {
      toast({ title: "Bulk update failed", description: err.message, variant: "destructive" });
    },
  });

  const publishArticles = useMutation({
    mutationFn: async ({ ids, websiteIds }: { ids: string[]; websiteIds: string[] }) => {
      const selectedArticles = articles.filter((article: any) => ids.includes(article.id));
      const results: { ok: boolean; articleTitle: string; siteName: string; message?: string }[] = [];
      let skipped = 0;

      for (const article of selectedArticles) {
        for (const websiteId of websiteIds) {
          const alreadyPublished = (article.article_publications || []).some((publication: any) =>
            publication.website_id === websiteId && publication.status === "published",
          );
          if (alreadyPublished) {
            skipped++;
            continue;
          }

          const site = websites.find((w) => w.id === websiteId);
          try {
            const { data, error } = await supabase.functions.invoke("publish-to-wordpress", {
              body: { article_id: article.id, website_id: websiteId, action: "publish" },
            });

            if (error || (data as any)?.error) {
              results.push({
                ok: false,
                articleTitle: article.title,
                siteName: site?.name || "Website",
                message: (data as any)?.error || error?.message || "Publishing failed",
              });
            } else {
              results.push({ ok: true, articleTitle: article.title, siteName: site?.name || "Website" });
            }
          } catch (error: any) {
            results.push({
              ok: false,
              articleTitle: article.title,
              siteName: site?.name || "Website",
              message: error?.message || "Publishing failed",
            });
          }
        }
      }

      return {
        total: results.length,
        success: results.filter((result) => result.ok).length,
        failure: results.filter((result) => !result.ok).length,
        skipped,
        firstError: results.find((result) => !result.ok),
      };
    },
    onSuccess: ({ total, success, failure, skipped, firstError }) => {
      setSelected(new Set());
      setPublishDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ["articles-library"] });

      if (total === 0) {
        toast({
          title: "Nothing to publish",
          description: skipped > 0 ? `${skipped} already-published destination(s) skipped.` : "No publishable destinations were selected.",
        });
        return;
      }

      if (failure === 0) {
        toast({
          title: `Published ${success}/${total} successfully`,
          description: skipped > 0 ? `${skipped} already-published destination(s) skipped.` : undefined,
        });
        return;
      }

      if (success === 0) {
        toast({
          title: "Publishing failed",
          description: firstError ? `${firstError.articleTitle} → ${firstError.siteName}: ${firstError.message}` : "Please try again.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: `${success} publication(s) completed`,
        description: firstError ? `${failure} failed. ${firstError.articleTitle} → ${firstError.siteName}: ${firstError.message}` : `${failure} failed.`,
      });
    },
    onError: (error: any) => {
      toast({ title: "Publishing failed", description: error.message, variant: "destructive" });
    },
  });

  const selectableArticles = useMemo(() => articles.filter((article: any) => !article.is_trial_demo), [articles]);
  const allOnPageSelected = selectableArticles.length > 0 && selectableArticles.every((a: any) => selected.has(a.id));
  const toggleAll = () => {
    if (allOnPageSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(selectableArticles.map((a: any) => a.id)));
    }
  };
  const toggleOne = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Row-level callbacks — stable references so ArticleRow's React.memo
  // shallow compare short-circuits when only an unrelated parent state
  // changes (filters, dialogs, etc).
  const handleChangeStatus = useCallback(
    (id: string, status: ArticleStatus) => {
      updateStatus.mutate({ id, status });
    },
    [updateStatus],
  );
  const handlePreview = useCallback((article: any) => setPreviewArticle(article), []);
  const handleSendToFactory = useCallback((id: string) => sendToFactory.mutate(id), [sendToFactory]);
  const handlePublishSingle = useCallback((id: string) => {
    setSelected(new Set([id]));
    setPublishDialogOpen(true);
  }, []);
  const handleDelete = useCallback((id: string) => singleDelete.mutate(id), [singleDelete]);

  // Build a map of website ids to their info for "View Live" links
  const websiteMap = useMemo(() => {
    const map: Record<string, { name: string; domain?: string }> = {};
    websites.forEach((w: any) => { map[w.id] = { name: w.name, domain: w.domain }; });
    return map;
  }, [websites]);

  const selectedArticles = useMemo(
    () => articles.filter((article: any) => selected.has(article.id)),
    [articles, selected],
  );

  const selectedLanguages = useMemo<string[]>(
    () => Array.from(new Set<string>(selectedArticles.map((article: any) => String(article.language || "")).filter(Boolean))).sort(),
    [selectedArticles],
  );

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t("articles.title")}</h1>
          <p className="text-sm text-muted-foreground">
            {totalCount} article{totalCount !== 1 ? "s" : ""} total
            {articleLimit != null && (
              <span className="ml-3 inline-block px-2 py-1 bg-muted rounded text-xs">
                Monthly: {usage.articles_created_this_month}/{articleLimit}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={backfillImages.isPending}
            onClick={() => {
              const ids = selected.size > 0 ? Array.from(selected) : null;
              backfillImages.mutate(ids);
            }}
            title="Find and assign images to articles missing a featured image"
          >
            {backfillImages.isPending
              ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              : <ImagePlus className="h-4 w-4 mr-1" />}
            {selected.size > 0 ? `Auto-Assign Images (${selected.size})` : "Auto-Assign Missing Images"}
          </Button>
          <Link to="/content-factory">
            <Button variant="outline" size="sm"><Factory className="h-4 w-4 mr-1" /> Pipeline</Button>
          </Link>
          {!canCreateArticles || (articleLimit != null && usage.articles_created_this_month >= articleLimit) ? (
            <Button
              size="sm"
              variant="outline"
              disabled
              title={isTrialPackage ? "Trial workspaces can review sample articles but cannot create new articles" : articleLimit != null ? `Limit reached: ${usage.articles_created_this_month}/${articleLimit}` : "No active package is attached to this workspace"}
            >
              <Plus className="h-4 w-4 mr-1" /> {t("articles.newArticle")}
            </Button>
          ) : (
            <Link to="/articles/new">
              <Button size="sm"><Plus className="h-4 w-4 mr-1" /> {t("articles.newArticle")}</Button>
            </Link>
          )}
        </div>
      </div>

      {/* Filters */}
      <ArticleFilters
        search={search}
        onSearchChange={(v) => setParam("q", v)}
        status={filterStatus}
        onStatusChange={(v) => setParam("status", v)}
        category={filterCategory}
        onCategoryChange={(v) => setParam("category", v)}
        website={filterWebsite}
        onWebsiteChange={(v) => setParam("website", v)}
        author={filterAuthor}
        onAuthorChange={(v) => setParam("author", v)}
        language={filterLanguage}
        onLanguageChange={(v) => setParam("lang", v)}
        categories={categories}
        websites={websites}
        authors={authors}
        perPage={perPage}
        onPerPageChange={(v) => setParam("perPage", String(v))}
      />

      {/* Table */}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full rounded" />
          ))}
        </div>
      ) : articles.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <h3 className="text-lg font-medium text-foreground">No articles found</h3>
          <p className="text-sm text-muted-foreground mt-1">{isTrialPackage ? "Trial workspaces include temporary examples only." : "Adjust your filters or create a new article."}</p>
          {canCreateArticles && (articleLimit == null || usage.articles_created_this_month < articleLimit) && (
            <Link to="/articles/new">
              <Button className="mt-4"><Plus className="mr-2 h-4 w-4" /> Create Article</Button>
            </Link>
          )}
          {(!canCreateArticles || (articleLimit != null && usage.articles_created_this_month >= articleLimit)) && (
            <div className="mt-4 p-4 bg-amber-100 border border-amber-300 rounded-lg text-amber-900">
              <p className="text-sm">
                {isTrialPackage ? "Trial accounts can review temporary records but cannot create new articles." : articleLimit != null ? `You've reached your monthly limit of ${articleLimit} articles.` : "No active package is attached to this workspace."}
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-lg border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/30">
                <TableHead className="w-10">
                  <Checkbox checked={allOnPageSelected} onCheckedChange={toggleAll} aria-label="Select all" />
                </TableHead>
                <TableHead className="w-14">{t("common.preview")}</TableHead>
                <TableHead>{t("common.title")}</TableHead>
                <TableHead>{t("common.status")}</TableHead>
                <TableHead className="text-right">{t("common.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {articles.map((article: any) => (
                <ArticleRow
                  key={article.id}
                  article={article}
                  isSelected={selected.has(article.id)}
                  updateStatusLoading={updateStatus.isPending && updateStatus.variables?.id === article.id}
                  onToggleSelect={toggleOne}
                  onChangeStatus={handleChangeStatus}
                  onPreview={handlePreview}
                  onSendToFactory={handleSendToFactory}
                  onPublishSingle={handlePublishSingle}
                  onDelete={handleDelete}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <p className="text-xs text-muted-foreground">
            Page {page} / {totalPages}
          </p>
          <div className="flex items-center gap-1">
            <IconButton variant="outline" className="h-8 w-8" disabled={page <= 1} onClick={() => setParam("page", String(page - 1))} label={t("actions.previousPage")}>
              <ChevronLeft className="h-4 w-4" />
            </IconButton>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let p: number;
              if (totalPages <= 5) p = i + 1;
              else if (page <= 3) p = i + 1;
              else if (page >= totalPages - 2) p = totalPages - 4 + i;
              else p = page - 2 + i;
              return (
                <IconButton key={p} variant={p === page ? "default" : "outline"} className="h-8 w-8 text-xs" onClick={() => setParam("page", String(p))} label={t("actions.gotoPage", { n: p })}>
                  {p}
                </IconButton>
              );
            })}
            <IconButton variant="outline" className="h-8 w-8" disabled={page >= totalPages} onClick={() => setParam("page", String(page + 1))} label={t("actions.nextPage")}>
              <ChevronRight className="h-4 w-4" />
            </IconButton>
          </div>
        </div>
      )}

      <ArticleBulkBar
        count={selected.size}
        onDelete={() => bulkDelete.mutate(Array.from(selected))}
        onClear={() => setSelected(new Set())}
        isDeleting={bulkDelete.isPending}
        onChangeStatus={(status) => bulkUpdateStatus.mutate({ ids: Array.from(selected), status })}
        isUpdatingStatus={bulkUpdateStatus.isPending}
        onPublish={() => setPublishDialogOpen(true)}
        isPublishing={publishArticles.isPending}
      />

      <ArticleQuickPreview
        article={previewArticle}
        open={!!previewArticle}
        onOpenChange={(open) => !open && setPreviewArticle(null)}
      />

      <MultiSitePublishDialog
        open={publishDialogOpen}
        onOpenChange={setPublishDialogOpen}
        websites={websites}
        articleCount={selectedArticles.length}
        articleLanguages={selectedLanguages}
        onConfirm={async (websiteIds) => {
          await publishArticles.mutateAsync({ ids: Array.from(selected), websiteIds });
        }}
      />
    </div>
  );
}
